/**
 * Browser controller that drives the collection grid: fetches filtered pages via
 * the engine, renders cards, and manages load-more + loading/empty states.
 *
 * DOM-only concerns live here; the engine and card renderer stay pure. The sidebar
 * calls `applySelection()` / `applySort()` and consumes `onFacets`.
 */

import { CollectionFilterEngine } from './index';
import { buildCardHtml } from './renderProductCard';
import { FacetGroup, FilterPageResult, PageContext, ProductNode } from './types';

const COUNT_DEBOUNCE_MS = 400;

export interface CollectionViewConfig {
  engine: CollectionFilterEngine;
  ctx: PageContext;
  gridEl: HTMLElement;
  statusEl?: HTMLElement | null;
  loadMoreEl?: HTMLElement | null;
  pageSize?: number;
  sortValue?: string;
  onFacets?: (facets: FacetGroup[]) => void;
  onPage?: (info: {
    rendered: number;
    hasNext: boolean;
    clientRefined: boolean;
    apiTotal: number | null;
    apiTotalCapped: boolean;
    /** Exact rendered-product total from the background count pass, once it lands. */
    exactTotal: number | null;
    /** The count pass hit its page budget; `exactTotal` is a floor, render it as "N+". */
    exactTotalCapped: boolean;
  }) => void;
  onCardsRendered?: (cards: HTMLElement[]) => void;
}

export class CollectionView {
  private cfg: CollectionViewConfig;
  private after: string | null = null;
  private hasNext = true;
  private loading = false;
  private rendered = 0;
  private sortValue?: string;
  private replacing = false;
  /** Bumped on every reset; in-flight responses from an older generation are discarded. */
  private generation = 0;
  private facetsShown = false;
  /** Product ids already on the page, so a hydrated first page and Load More can't
   *  render the same product twice. */
  private shownIds: { [id: string]: boolean } = {};
  /** Last page's totals, replayed by `emitPage()` when the count pass resolves. */
  private lastClientRefined = false;
  private lastApiTotal: number | null = null;
  private lastApiTotalCapped = false;
  private exactTotal: { total: number; capped: boolean } | null = null;
  private countStarted = false;
  private countTimer: number | null = null;

  constructor(cfg: CollectionViewConfig) {
    this.cfg = cfg;
    this.sortValue = cfg.sortValue;
    if (cfg.loadMoreEl) {
      cfg.loadMoreEl.addEventListener('click', () => this.loadNext());
    }
  }

  start(): Promise<void> {
    return this.reset();
  }

  /**
   * Adopt server-rendered first-page cards instead of fetching + rendering them (used on
   * the pristine collection URL, so the first paint and crawlers get real product HTML).
   * Page 1 is still fetched, but only to arm the sidebar facets and the Load-More cursor.
   * Any filter/sort interaction supersedes this and falls through to a replacing load.
   */
  hydrate(): Promise<void> {
    this.generation++;
    const gen = this.generation;
    const serverCards = this.gridCards();
    this.rendered = serverCards.length;
    for (let i = 0; i < serverCards.length; i++) {
      const idEl = serverCards[i].querySelector('[data-product-id]');
      const id = idEl && idEl.getAttribute('data-product-id');
      if (id) this.shownIds[id] = true;
    }
    this.replacing = false;
    this.loading = true;
    if (this.cfg.onCardsRendered && serverCards.length) this.cfg.onCardsRendered(serverCards);

    return this.cfg.engine
      .fetchPage({ sortValue: this.sortValue, pageSize: this.cfg.pageSize, after: null })
      .then((result: FilterPageResult) => {
        this.loading = false;
        if (gen !== this.generation) return this.loadNext();
        this.after = result.endCursor;
        this.hasNext = result.hasNextPage;
        if (!this.facetsShown && result.facets && result.facets.length && this.cfg.onFacets) {
          this.cfg.onFacets(result.facets);
          this.facetsShown = true;
        }
        this.updateControls();
        this.recordTotals(result);
        this.emitPage();
      })
      .catch((err) => {
        this.loading = false;
        if (gen !== this.generation) return this.loadNext();
        this.hasNext = false;
        this.updateControls();
        if (typeof console !== 'undefined') console.error('Collection hydrate failed', err);
      });
  }

  private gridCards(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const kids = this.cfg.gridEl.children;
    for (let i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 1) out.push(kids[i] as HTMLElement);
    }
    return out;
  }

  applySelection(): Promise<void> {
    return this.reset();
  }

  applySort(sortValue: string): Promise<void> {
    this.sortValue = sortValue;
    return this.reset();
  }

  private reset(): Promise<void> {
    this.generation++;
    this.after = null;
    this.hasNext = true;
    this.rendered = 0;
    this.replacing = true;
    this.facetsShown = false;
    this.shownIds = {};
    this.exactTotal = null;
    this.countStarted = false;
    if (this.countTimer !== null) {
      clearTimeout(this.countTimer);
      this.countTimer = null;
    }
    if (this.cfg.gridEl.children.length > 0) {
      this.cfg.gridEl.classList.add('sf-refreshing');
    } else {
      this.setStatus('loading');
    }
    if (this.loading) return Promise.resolve();
    return this.loadNext();
  }

  loadNext(): Promise<void> {
    if (this.loading) return Promise.resolve();
    if (!this.replacing && !this.hasNext) return Promise.resolve();
    const gen = this.generation;
    this.loading = true;
    if (!this.replacing) this.setStatus('loading');
    const after = this.after;

    return this.cfg.engine
      .fetchPage({ sortValue: this.sortValue, pageSize: this.cfg.pageSize, after })
      .then((result: FilterPageResult) => {
        this.loading = false;
        if (gen !== this.generation) return this.loadNext(); // superseded — load current selection
        this.after = result.endCursor;
        this.hasNext = result.hasNextPage;
        if (this.replacing) {
          this.cfg.gridEl.innerHTML = '';
          this.cfg.gridEl.classList.remove('sf-refreshing');
          this.replacing = false;
        }
        if (!this.facetsShown && result.facets && result.facets.length && this.cfg.onFacets) {
          this.cfg.onFacets(result.facets);
          this.facetsShown = true;
        }
        this.rendered += this.appendCards(result.products);
        this.updateControls();
        this.recordTotals(result);
        this.emitPage();
        this.maybeCount(gen);
        // Sparse refinement: nothing survived this page but more remain — keep paging so
        // the grid isn't left empty when matches exist deeper in the collection.
        if (this.rendered === 0 && this.hasNext) return this.loadNext();
      })
      .catch((err) => {
        this.loading = false;
        if (gen !== this.generation) return this.loadNext();
        this.replacing = false;
        this.cfg.gridEl.classList.remove('sf-refreshing');
        this.setStatus('error');
        if (typeof console !== 'undefined') console.error('Collection load failed', err);
      });
  }

  private recordTotals(result: FilterPageResult): void {
    this.lastClientRefined = result.clientRefined;
    this.lastApiTotal = result.apiTotal;
    this.lastApiTotalCapped = result.apiTotalCapped;
  }

  private emitPage(): void {
    if (!this.cfg.onPage) return;
    this.cfg.onPage({
      rendered: this.rendered,
      hasNext: this.hasNext,
      clientRefined: this.lastClientRefined,
      apiTotal: this.lastApiTotal,
      apiTotalCapped: this.lastApiTotalCapped,
      exactTotal: this.exactTotal ? this.exactTotal.total : null,
      exactTotalCapped: !!(this.exactTotal && this.exactTotal.capped),
    });
  }

  /**
   * Client refinement makes the API's own total a superset of what renders, so the only
   * honest number is a full scan. Run it once per selection, after the first page has
   * painted, and re-emit the label when it lands — the grid never waits on it.
   */
  private maybeCount(gen: number): void {
    if (this.countStarted || !this.lastClientRefined || !this.hasNext) return;
    this.countStarted = true;
    // Debounced: a scan can run to several requests, so let a burst of filter clicks
    // settle and only count the selection the shopper actually lands on.
    this.countTimer = setTimeout(() => {
      this.countTimer = null;
      if (gen !== this.generation) return;
      this.cfg.engine
        .countTotal()
        .then((res) => {
          if (gen !== this.generation) return;
          this.exactTotal = res;
          this.emitPage();
        })
        .catch((err) => {
          // Leave the "N+" fallback in place; the grid is unaffected.
          if (typeof console !== 'undefined') console.warn('Collection count failed', err);
        });
    }, COUNT_DEBOUNCE_MS) as unknown as number;
  }

  private appendCards(products: ProductNode[]): number {
    if (products.length === 0) return 0;
    let html = '';
    let added = 0;
    for (let i = 0; i < products.length; i++) {
      const id = String(products[i].id || '').split('/').pop() || '';
      if (id && this.shownIds[id]) continue; // already rendered (server page or earlier load)
      if (id) this.shownIds[id] = true;
      html += buildCardHtml(products[i], this.cfg.ctx);
      added++;
    }
    if (added === 0) return 0;
    const frag = document.createElement('div');
    frag.innerHTML = html;
    const inserted: HTMLElement[] = [];
    while (frag.firstChild) {
      const child = frag.firstChild;
      if (child.nodeType === 1) inserted.push(child as HTMLElement);
      this.cfg.gridEl.appendChild(child);
    }
    if (this.cfg.onCardsRendered && inserted.length) this.cfg.onCardsRendered(inserted);
    return added;
  }

  private updateControls(): void {
    if (this.cfg.loadMoreEl) {
      this.cfg.loadMoreEl.style.display = this.hasNext ? '' : 'none';
    }
    if (this.rendered === 0 && !this.hasNext) this.setStatus('empty');
    else this.setStatus('idle');
  }

  private setStatus(state: 'loading' | 'idle' | 'empty' | 'error'): void {
    const el = this.cfg.statusEl;
    if (!el) return;
    switch (state) {
      case 'loading':
        el.style.display = '';
        el.innerHTML = '<div class="loading">Loading products…</div>';
        break;
      case 'empty':
        el.style.display = '';
        el.innerHTML =
          '<div class="no-merch"><h2>Oops! No merch found.</h2>' +
          '<p>It’s not an e-merch-ancy. Try adjusting your filters.</p></div>';
        break;
      case 'error':
        el.style.display = '';
        el.innerHTML = '<div class="no-merch"><p>Something went wrong loading products.</p></div>';
        break;
      default:
        el.style.display = 'none';
        el.innerHTML = '';
    }
  }
}
